import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Network, Layers, Globe, Wifi, ChevronRight, ChevronDown, 
  AlertTriangle, Search, Plus, FolderTree, BarChart3, Building2,
  Server, Monitor, Printer, Router, Shield, HardDrive, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { isCapacityAlert } from './ipamCapacity';

const IPAMOverview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('site'); // 'site', 'network', 'dashboard'
  
  // Data
  const [sites, setSites] = useState([]);
  const [domains, setDomains] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [subnets, setSubnets] = useState([]);
  const [stats, setStats] = useState({
    totalSites: 0,
    totalDomains: 0,
    totalVlans: 0,
    totalSubnets: 0,
    totalIps: 0,
    criticalSubnets: []
  });
  
  // Tree state - Sites
  const [expandedSites, setExpandedSites] = useState({});
  
  // Tree state - Network
  const [expandedDomains, setExpandedDomains] = useState({});
  const [expandedVlans, setExpandedVlans] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch data individually to handle partial failures
      let sitesData = [];
      let domainsData = [];
      let vlansData = [];
      let subnetsData = [];

      try {
        const sitesRes = await api.get('/config/sites?limit=100');
        sitesData = sitesRes.data.data || sitesRes.data || [];
      } catch (e) {
        console.error('Error fetching sites:', e);
      }

      try {
        const domainsRes = await api.get('/ipam/l2-domains?limit=100');
        domainsData = domainsRes.data.data || [];
      } catch (e) {
        console.error('Error fetching L2 domains:', e);
      }

      try {
        const vlansRes = await api.get('/ipam/vlans?limit=100');
        vlansData = vlansRes.data.data || [];
      } catch (e) {
        console.error('Error fetching VLANs:', e);
      }

      try {
        const subnetsRes = await api.get('/ipam/subnets?limit=100');
        subnetsData = subnetsRes.data.data || [];
      } catch (e) {
        console.error('Error fetching subnets:', e);
      }

      setSites(sitesData);
      setDomains(domainsData);
      setVlans(vlansData);
      setSubnets(subnetsData);

      // Calculate stats
      const critical = subnetsData.filter(isCapacityAlert);
      const sitesWithSubnets = new Set(subnetsData.map(s => s.site_id).filter(Boolean));
      
      setStats({
        totalSites: sitesWithSubnets.size,
        totalDomains: domainsData.length,
        totalVlans: vlansData.length,
        totalSubnets: subnetsData.length,
        totalIps: subnetsData.reduce((acc, s) => acc + (s.used_ips || 0), 0),
        criticalSubnets: critical.slice(0, 5)
      });

      // Auto-expand first site with subnets
      const firstSiteWithSubnets = sitesData.find(s => 
        subnetsData.some(sub => sub.site_id === s.id)
      );
      if (firstSiteWithSubnets) {
        setExpandedSites({ [firstSiteWithSubnets.id]: true });
      }

      // Auto-expand first domain
      if (domainsData.length > 0) {
        setExpandedDomains({ [domainsData[0].id]: true });
      }
    } catch (error) {
      console.error('Error general fetching IPAM data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Toggle functions
  const toggleSite = (siteId) => {
    setExpandedSites(prev => ({ ...prev, [siteId]: !prev[siteId] }));
  };

  const toggleDomain = (domainId) => {
    setExpandedDomains(prev => ({ ...prev, [domainId]: !prev[domainId] }));
  };

  const toggleVlan = (vlanId) => {
    setExpandedVlans(prev => ({ ...prev, [vlanId]: !prev[vlanId] }));
  };

  // Get subnets for a site
  const getSubnetsForSite = (siteId) => {
    return subnets.filter(s => s.site_id === siteId);
  };

  // Get sites with subnets
  const getSitesWithSubnets = () => {
    const siteIds = new Set(subnets.map(s => s.site_id).filter(Boolean));
    return sites.filter(s => siteIds.has(s.id));
  };

  // Get subnets without site (orphaned)
  const getOrphanedSubnets = () => {
    return subnets.filter(s => !s.site_id);
  };

  // Get subnets without VLAN (orphaned in network view)
  const getSubnetsWithoutVlan = () => {
    return subnets.filter(s => !s.vlan_id);
  };

  // Get VLANs for a domain
  const getVlansForDomain = (domainId) => {
    return vlans.filter(v => v.l2_domain_id === domainId);
  };

  // Get Subnets for a VLAN
  const getSubnetsForVlan = (vlanId) => {
    return subnets.filter(s => s.vlan_id === vlanId);
  };

  // Utilization bar component
  const UtilizationBar = ({ percent, size = 'md' }) => {
    const getColor = (p) => {
      if (p >= 90) return 'bg-red-500';
      if (p >= 75) return 'bg-yellow-500';
      if (p >= 50) return 'bg-blue-500';
      return 'bg-green-500';
    };
    
    const heights = { sm: 'h-1.5', md: 'h-2', lg: 'h-3' };
    
    return (
      <div className={`w-full bg-muted rounded-full ${heights[size]} overflow-hidden`}>
        <div 
          className={`${getColor(percent)} ${heights[size]} rounded-full transition-all`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    );
  };

  // Filter by search
  const filterBySearch = (items, getLabel) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      getLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  const sitesWithSubnets = getSitesWithSubnets();
  const orphanedSubnets = getOrphanedSubnets();

  return (
    <Layout>
      <div className="space-y-6" data-testid="ipam-overview">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderTree className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">IPAM Overview</h1>
              <p className="text-sm text-muted-foreground">Gestión Visual de Direccionamiento IP</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/ipam/l2-domains">
              <Button variant="outline" size="sm">
                <Network className="h-4 w-4 mr-2" />
                L2 Domains
              </Button>
            </Link>
            <Link to="/ipam/vlans">
              <Button variant="outline" size="sm">
                <Layers className="h-4 w-4 mr-2" />
                VLANs
              </Button>
            </Link>
            <Link to="/ipam/subnets">
              <Button variant="outline" size="sm">
                <Globe className="h-4 w-4 mr-2" />
                Subnets
              </Button>
            </Link>
            <Link to="/ipam/addresses">
              <Button variant="outline" size="sm">
                <Wifi className="h-4 w-4 mr-2" />
                IPs
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="stats-card flex items-center gap-3">
            <Building2 className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div><div className="stats-card-value">{stats.totalSites}</div><div className="stats-card-label">Sites con Subnets</div></div>
          </div>
          <div className="stats-card flex items-center gap-3 cursor-pointer hover:border-primary/50" onClick={() => navigate('/ipam/l2-domains')}>
            <Network className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div><div className="stats-card-value">{stats.totalDomains}</div><div className="stats-card-label">L2 Domains</div></div>
          </div>
          <div className="stats-card flex items-center gap-3 cursor-pointer hover:border-primary/50" onClick={() => navigate('/ipam/vlans')}>
            <Layers className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div><div className="stats-card-value">{stats.totalVlans}</div><div className="stats-card-label">VLANs</div></div>
          </div>
          <div className="stats-card flex items-center gap-3 cursor-pointer hover:border-primary/50" onClick={() => navigate('/ipam/subnets')}>
            <Globe className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div><div className="stats-card-value">{stats.totalSubnets}</div><div className="stats-card-label">Subnets</div></div>
          </div>
          <div className="stats-card flex items-center gap-3 cursor-pointer hover:border-primary/50" onClick={() => navigate('/ipam/addresses')}>
            <Wifi className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div><div className="stats-card-value">{stats.totalIps}</div><div className="stats-card-label">IPs Registradas</div></div>
          </div>
        </div>

        {/* Main Content with Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="site" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Por Sitio
            </TabsTrigger>
            <TabsTrigger value="network" className="flex items-center gap-2">
              <Network className="h-4 w-4" />
              Por Red
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
          </TabsList>

          {/* TAB: Por Sitio */}
          <TabsContent value="site" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Site Tree */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Árbol por Sitio
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar sitio o subnet..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {sitesWithSubnets.length === 0 && orphanedSubnets.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay Subnets configuradas</p>
                      <Link to="/ipam/subnets">
                        <Button variant="outline" size="sm" className="mt-2">
                          <Plus className="h-4 w-4 mr-1" /> Crear Subnet
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {/* Sites with subnets */}
                      {filterBySearch(sitesWithSubnets, s => s.site).map((site) => {
                        const siteSubnets = getSubnetsForSite(site.id);
                        const totalUsage = siteSubnets.reduce((acc, s) => acc + (s.occupied_ips || 0), 0);
                        const totalCapacity = siteSubnets.reduce((acc, s) => acc + (s.total_hosts || 0), 0);
                        const avgUtilization = totalCapacity > 0 ? Math.round((totalUsage / totalCapacity) * 100) : 0;

                        return (
                          <div key={site.id} className="select-none">
                            {/* Site Level */}
                            <div
                              className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer group"
                              onClick={() => toggleSite(site.id)}
                            >
                              {expandedSites[site.id] ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <Building2 className="h-5 w-5 text-cyan-500" />
                              <span className="font-medium flex-1">{site.site}</span>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>{siteSubnets.length} subnets</span>
                                <div className="w-16">
                                  <UtilizationBar percent={avgUtilization} size="sm" />
                                </div>
                                <span className="w-8 text-right">{avgUtilization}%</span>
                              </div>
                            </div>

                            {/* Subnets under Site */}
                            {expandedSites[site.id] && (
                              <div className="ml-6 border-l border-border/50 pl-3 space-y-1 py-1">
                                {siteSubnets.map((subnet) => (
                                  <Link
                                    key={subnet.id}
                                    to={`/ipam/subnets/${subnet.id}`}
                                    className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 transition-colors group"
                                  >
                                    <Globe className="h-4 w-4 text-green-500" />
                                    <span className="font-mono text-sm font-medium text-primary">
                                      {subnet.network}/{subnet.mask}
                                    </span>
                                    {subnet.vlan_number && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                        VLAN {subnet.vlan_number}
                                      </span>
                                    )}
                                    {subnet.description && (
                                      <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                        {subnet.description}
                                      </span>
                                    )}
                                    <div className="flex-1" />
                                    <div className="flex items-center gap-2">
                                      <div className="w-20">
                                        <UtilizationBar percent={subnet.utilization || 0} size="sm" />
                                      </div>
                                      <span className={cn(
                                        "text-xs font-medium w-10 text-right",
                                        (subnet.utilization || 0) >= 90 ? "text-red-500" :
                                        (subnet.utilization || 0) >= 75 ? "text-yellow-500" : "text-muted-foreground"
                                      )}>
                                        {subnet.utilization || 0}%
                                      </span>
                                      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Orphaned Subnets (no site) */}
                      {orphanedSubnets.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <div 
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                            onClick={() => setExpandedSites(prev => ({ ...prev, orphaned: !prev.orphaned }))}
                          >
                            {expandedSites.orphaned ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            <span className="font-medium text-yellow-600 dark:text-yellow-400">
                              Sin Sitio Asignado
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {orphanedSubnets.length} subnets
                            </span>
                          </div>
                          
                          {expandedSites.orphaned && (
                            <div className="ml-6 border-l border-yellow-500/30 pl-3 space-y-1 py-1">
                              {orphanedSubnets.slice(0, 20).map((subnet) => (
                                <Link
                                  key={subnet.id}
                                  to={`/ipam/subnets/${subnet.id}`}
                                  className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
                                >
                                  <Globe className="h-4 w-4 text-yellow-500" />
                                  <span className="font-mono text-sm">{subnet.network}/{subnet.mask}</span>
                                  {subnet.vlan_number && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600">
                                      VLAN {subnet.vlan_number}
                                    </span>
                                  )}
                                </Link>
                              ))}
                              {orphanedSubnets.length > 20 && (
                                <p className="text-xs text-muted-foreground p-2">
                                  ... y {orphanedSubnets.length - 20} más
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats Panel */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Alertas de Capacidad
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {stats.criticalSubnets.length === 0 ? (
                    <div className="text-sm text-muted-foreground bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                      ✓ No hay subnets críticas
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {stats.criticalSubnets.map((subnet) => (
                        <Link
                          key={subnet.id}
                          to={`/ipam/subnets/${subnet.id}`}
                          className="flex items-center gap-3 p-2 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-colors"
                        >
                          <Globe className="h-4 w-4 text-red-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm truncate">{subnet.network}/{subnet.mask}</div>
                            <div className="text-xs text-muted-foreground">{subnet.site_name || 'Sin sitio'}</div>
                          </div>
                          <span className="text-red-500 font-bold text-sm">{subnet.utilization}%</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Legend */}
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Leyenda de Uso</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-green-500"></div>
                        <span>&lt;50%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-blue-500"></div>
                        <span>50-74%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-yellow-500"></div>
                        <span>75-89%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-red-500"></div>
                        <span>≥90%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Por Red */}
          <TabsContent value="network" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Network Tree */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Network className="h-4 w-4 text-primary" />
                    Árbol por Red (L2 Domain → VLAN → Subnet)
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {domains.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Network className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay L2 Domains</p>
                      <Link to="/ipam/l2-domains">
                        <Button variant="outline" size="sm" className="mt-2">
                          <Plus className="h-4 w-4 mr-1" /> Crear
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {domains.map((domain) => (
                        <div key={domain.id} className="select-none">
                          {/* Domain Level */}
                          <div
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer group"
                            onClick={() => toggleDomain(domain.id)}
                          >
                            {expandedDomains[domain.id] ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <Network className="h-5 w-5 text-cyan-500" />
                            <span className="font-medium flex-1">{domain.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {getVlansForDomain(domain.id).length} VLANs
                            </span>
                          </div>

                          {/* VLANs Level */}
                          {expandedDomains[domain.id] && (
                            <div className="ml-6 border-l border-border/50 pl-3 space-y-1">
                              {getVlansForDomain(domain.id).map((vlan) => (
                                <div key={vlan.id}>
                                  <div
                                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer group"
                                    onClick={() => toggleVlan(vlan.id)}
                                  >
                                    {expandedVlans[vlan.id] ? (
                                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                    )}
                                    <Layers className="h-4 w-4 text-blue-500" />
                                    <span className="font-mono font-bold text-primary">{vlan.vlan_number}</span>
                                    <span className="text-sm text-muted-foreground">- {vlan.name}</span>
                                    <span className="text-xs text-muted-foreground ml-auto">
                                      {getSubnetsForVlan(vlan.id).length} subnets
                                    </span>
                                  </div>

                                  {/* Subnets Level */}
                                  {expandedVlans[vlan.id] && (
                                    <div className="ml-5 border-l border-border/30 pl-3 space-y-0.5">
                                      {getSubnetsForVlan(vlan.id).map((subnet) => (
                                        <Link
                                          key={subnet.id}
                                          to={`/ipam/subnets/${subnet.id}`}
                                          className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50 cursor-pointer group text-sm"
                                        >
                                          <Globe className="h-3 w-3 text-green-500" />
                                          <span className="font-mono text-xs">{subnet.network}/{subnet.mask}</span>
                                          {subnet.site_name && (
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                              <MapPin className="h-2.5 w-2.5 inline mr-0.5" />
                                              {subnet.site_name}
                                            </span>
                                          )}
                                          <div className="flex-1 ml-2">
                                            <UtilizationBar percent={subnet.utilization || 0} size="sm" />
                                          </div>
                                          <span className="text-xs text-muted-foreground">
                                            {subnet.utilization || 0}%
                                          </span>
                                        </Link>
                                      ))}
                                      {getSubnetsForVlan(vlan.id).length === 0 && (
                                        <div className="text-xs text-muted-foreground p-2 italic">
                                          Sin subnets
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {getVlansForDomain(domain.id).length === 0 && (
                                <div className="text-xs text-muted-foreground p-2 italic">
                                  Sin VLANs
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Subnets without VLAN (orphaned in network view) */}
                      {getSubnetsWithoutVlan().length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <div 
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                            onClick={() => setExpandedDomains(prev => ({ ...prev, noVlan: !prev.noVlan }))}
                          >
                            {expandedDomains.noVlan ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            <span className="font-medium text-yellow-600 dark:text-yellow-400">
                              Sin VLAN Asignada
                            </span>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {getSubnetsWithoutVlan().length} subnets
                            </span>
                          </div>
                          
                          {expandedDomains.noVlan && (
                            <div className="ml-6 border-l border-yellow-500/30 pl-3 space-y-1 py-1">
                              {getSubnetsWithoutVlan().slice(0, 20).map((subnet) => (
                                <Link
                                  key={subnet.id}
                                  to={`/ipam/subnets/${subnet.id}`}
                                  className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
                                >
                                  <Globe className="h-4 w-4 text-yellow-500" />
                                  <span className="font-mono text-sm">{subnet.network}/{subnet.mask}</span>
                                  {subnet.site_name && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                      <MapPin className="h-2.5 w-2.5 inline mr-0.5" />
                                      {subnet.site_name}
                                    </span>
                                  )}
                                  <div className="flex-1 ml-2">
                                    <UtilizationBar percent={subnet.utilization || 0} size="sm" />
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {subnet.utilization || 0}%
                                  </span>
                                </Link>
                              ))}
                              {getSubnetsWithoutVlan().length > 20 && (
                                <p className="text-xs text-muted-foreground p-2">
                                  ... y {getSubnetsWithoutVlan().length - 20} más
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stats Panel */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Resumen de Red
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                      <span className="text-sm">L2 Domains</span>
                      <span className="font-bold text-primary">{stats.totalDomains}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                      <span className="text-sm">VLANs</span>
                      <span className="font-bold text-primary">{stats.totalVlans}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                      <span className="text-sm">Subnets</span>
                      <span className="font-bold text-primary">{stats.totalSubnets}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                      <span className="text-sm">IPs Registradas</span>
                      <span className="font-bold text-primary">{stats.totalIps}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB: Dashboard */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Subnets by Usage */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Top 10 Subnets por Uso
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {subnets
                      .sort((a, b) => (b.utilization || 0) - (a.utilization || 0))
                      .slice(0, 10)
                      .map((subnet, index) => (
                        <Link
                          key={subnet.id}
                          to={`/ipam/subnets/${subnet.id}`}
                          className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 transition-colors"
                        >
                          <span className="text-xs text-muted-foreground w-5">{index + 1}</span>
                          <span className="font-mono text-sm flex-1">{subnet.network}/{subnet.mask}</span>
                          <span className="text-xs text-muted-foreground">{subnet.site_name || '-'}</span>
                          <div className="w-24">
                            <UtilizationBar percent={subnet.utilization || 0} size="sm" />
                          </div>
                          <span className={cn(
                            "text-sm font-medium w-12 text-right",
                            (subnet.utilization || 0) >= 90 ? "text-red-500" :
                            (subnet.utilization || 0) >= 75 ? "text-yellow-500" : "text-green-500"
                          )}>
                            {subnet.utilization || 0}%
                          </span>
                        </Link>
                      ))}
                    {subnets.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay subnets
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sites Summary */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    Subnets por Sitio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sitesWithSubnets
                      .sort((a, b) => getSubnetsForSite(b.id).length - getSubnetsForSite(a.id).length)
                      .slice(0, 10)
                      .map((site) => {
                        const siteSubnets = getSubnetsForSite(site.id);
                        return (
                          <div
                            key={site.id}
                            className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer"
                            onClick={() => {
                              setActiveTab('site');
                              setExpandedSites({ [site.id]: true });
                            }}
                          >
                            <Building2 className="h-4 w-4 text-cyan-500" />
                            <span className="flex-1 font-medium">{site.site}</span>
                            <span className="text-sm text-primary font-bold">{siteSubnets.length}</span>
                            <span className="text-xs text-muted-foreground">subnets</span>
                          </div>
                        );
                      })}
                    {sitesWithSubnets.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay sitios con subnets
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default IPAMOverview;
